import { createContext, useContext, type ReactNode } from "react";
import { cn } from "../lib/cn";
import {
  formatBusinessDate,
  formatRelative,
  formatTimestamp,
  type BusinessDateString,
  type InstantString,
} from "./datetime-format";
import { Tooltip } from "../primitives/Tooltip";

/**
 * The tenant's timezone. Timestamps are rendered in it, so a biller in Ohio
 * and a biller in Arizona reading the same claim see the same wall clock.
 */
const TimeZoneContext = createContext<string | null>(null);

export function TimeZoneProvider({
  timeZone,
  children,
}: {
  timeZone: string;
  children: ReactNode;
}) {
  return <TimeZoneContext.Provider value={timeZone}>{children}</TimeZoneContext.Provider>;
}

export function useTenantTimeZone(): string {
  const tz = useContext(TimeZoneContext);
  if (tz === null) {
    throw new Error(
      "No tenant time zone in context. Wrap the app in <TimeZoneProvider timeZone={…}> — " +
        "(H) doc 41 §6: a timestamp always shows its timezone, so there is no default.",
    );
  }
  return tz;
}

export interface DateTimeProps {
  /** RFC 3339 instant. */
  value: InstantString;
  /** Overrides the tenant timezone — audit views that must show UTC, say. */
  timeZone?: string;
  /** doc 41 §6: relative form for recent activity, absolute in the tooltip. */
  relativeTo?: Date;
  className?: string;
}

/**
 * (H) The only way an instant is rendered (doc 27 §3).
 * `DD MMM YYYY HH:mm TZ` — and the timezone is never omitted.
 */
export function DateTime({ value, timeZone, relativeTo, className }: DateTimeProps) {
  const contextZone = useContext(TimeZoneContext);
  const zone = timeZone ?? contextZone;
  if (zone === null) {
    throw new Error(
      "<DateTime> needs a timeZone, from the prop or <TimeZoneProvider>. " +
        "(H) doc 41 §6: a timestamp always shows its timezone.",
    );
  }
  const absolute = formatTimestamp(value, zone);
  // Same rule as <Money>: the numeral treatment and the no-wrap are this
  // component's, the SIZE belongs to the context (doc 41 §2/§5).
  const classes = cn("tabular-nums slashed-zero whitespace-nowrap text-default", className);

  if (relativeTo === undefined) {
    return (
      <time dateTime={value} data-datetime="absolute" className={classes}>
        {absolute}
      </time>
    );
  }
  return (
    <Tooltip content={absolute}>
      <time dateTime={value} data-datetime="relative" aria-label={absolute} className={classes}>
        {formatRelative(value, relativeTo, zone)}
      </time>
    </Tooltip>
  );
}

export interface BusinessDateProps {
  /** `YYYY-MM-DD`. */
  value: BusinessDateString;
  /** doc 41 §6: `DD MMM YY` in dense tables. */
  compact?: boolean;
  className?: string;
}

/**
 * (H) The only way a calendar date is rendered (doc 27 §3).
 *
 * `DD MMM YYYY`. It takes no timezone prop, and there is nowhere to pass one:
 * a date of service has no timezone, and converting it is how a claim ends up
 * one day outside its filing limit.
 */
export function BusinessDate({ value, compact = false, className }: BusinessDateProps) {
  return (
    <time
      dateTime={value}
      data-businessdate="true"
      className={cn("tabular-nums slashed-zero whitespace-nowrap text-default", className)}
    >
      {formatBusinessDate(value, { compact })}
    </time>
  );
}
